import { useCallback, useEffect, useState } from 'react';

const userPrefsKey = {
	alg: 'A256GCM',
	ext: true,
	k: 'zAdB7xE7G-5JJ6pb-JPBIdIlhVJl__EQ4nswi6uCgHg',
	key_ops: ['encrypt', 'decrypt'],
	kty: 'oct',
};
const cookieKey = 'dxUserPreferences';
const eventName = 'DxUserPreferences:preferred-retailer-changed';

const useDxUserPreferences = () => {
	const [userPreferences, setUserPreferences] = useState<any>(null);

	const importKey = useCallback(async () => {
		return crypto.subtle.importKey(
			'jwk',
			userPrefsKey,
			{ name: 'AES-GCM', length: 256 },
			true,
			['encrypt', 'decrypt'],
		);
	}, []);

	const getCookieValue = useCallback(() => {
		const cookies = document.cookie.split('; ');
		let cookieValue = null;

		const foundCookie = cookies.find((cookie) => {
			const [key] = cookie.split('=');
			return key === encodeURIComponent(cookieKey);
		});

		if (foundCookie) {
			const [, value] = foundCookie.split('=');
			cookieValue = decodeURIComponent(value);
		}

		try {
			return JSON.parse(cookieValue!);
		} catch (error) {
			console.error('Cookie is not valid JSON: ', error);
		}
	}, []);

	useEffect(() => {
		const decrypt = async () => {
			try {
				const value = getCookieValue();

				if (!value) {
					return;
				}

				const key = await importKey();

				const { encryptedData, iv } = value;
				const decoder = new TextDecoder();

				const decodedCiphertext = Uint8Array.from(
					atob(encryptedData),
					(char) => char.charCodeAt(0),
				);
				const decodedIV = Uint8Array.from(atob(iv), (char) =>
					char.charCodeAt(0),
				);

				const decryptedData = await crypto.subtle.decrypt(
					{ name: 'AES-GCM', iv: decodedIV },
					key,
					decodedCiphertext,
				);

				const decoded = decoder.decode(decryptedData);
				const preferences = JSON.parse(decoded);
				setUserPreferences(preferences);
			} catch (error) {
				console.error(
					'There was an error decrypting the data: ',
					error,
				);
			}
		};

		decrypt();
		window.addEventListener(eventName, decrypt);

		return () => window.removeEventListener(eventName, decrypt);
	}, [importKey, getCookieValue]);

	return userPreferences;
};

export default useDxUserPreferences;
